import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Flask, Download, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import RequisitionFileUploadDialog from '@/components/RequisitionFileUploadDialog';
import RequisitionDeleteButton from '@/components/RequisitionDeleteButton';
import SearchFilterBar, { filterRecords } from '@/components/SearchFilterBar';

const REQ_STATUS_OPTIONS = [
  { value: 'pending_quotation', label: 'Pending Quotation' },
  { value: 'quotations_added', label: 'Quotations Added' },
  { value: 'vendor_approved', label: 'Vendor Approved' },
  { value: 'po_created', label: 'PO Created' },
  { value: 'po_released', label: 'PO Released' },
  { value: 'completed', label: 'Completed' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployeeDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [requisitions, setRequisitions] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [open, setOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    reagent_name: '',
    vendor_name: '',
    catalogue_number: '',
    quantity: 1,
    project_code_id: ''
  });

  const loadData = async () => {
    try {
      const [reqRes, pcRes] = await Promise.all([
        axios.get(`${API}/requisitions`, { headers: getAuthHeader() }),
        axios.get(`${API}/project-codes`, { headers: getAuthHeader() })
      ]);
      setRequisitions(reqRes.data);
      setProjectCodes(pcRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredRequisitions = useMemo(
    () =>
      filterRecords(requisitions, {
        keyword,
        statusValue: statusFilter,
        startDate,
        endDate,
        fields: ['reagent_name', 'vendor_name', 'catalogue_number', 'project_code_name'],
        dateField: 'created_at',
      }),
    [requisitions, keyword, statusFilter, startDate, endDate]
  );

  const actuallyCreateRequisition = async () => {
    try {
      await axios.post(`${API}/requisitions`, formData, { headers: getAuthHeader() });
      toast.success('Requisition created successfully');
      setOpen(false);
      setFormData({ reagent_name: '', vendor_name: '', catalogue_number: '', quantity: 1, project_code_id: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create requisition');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Duplicate detection is now handled by the Operations Manager during vendor approval —
    // employees are never blocked from placing an order.
    await actuallyCreateRequisition();
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending_quotation: { label: 'Pending Quotation', class: 'status-pending' },
      quotations_added: { label: 'Quotations Added', class: 'status-info' },
      vendor_approved: { label: 'Vendor Approved', class: 'status-approved' },
      po_created: { label: 'PO Created', class: 'status-info' },
      po_released: { label: 'PO Released', class: 'status-approved' },
      completed: { label: 'Completed', class: 'status-approved' }
    };
    const statusInfo = statusMap[status] || { label: status, class: 'status-info' };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const handleExportRequisition = async (reqId) => {
    try {
      const response = await axios.get(`${API}/requisitions/${reqId}/export`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `requisition_${reqId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Requisition exported successfully');
    } catch (error) {
      toast.error('Failed to export requisition');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-text-primary">
              My Requisitions
            </h1>
            <p className="mt-2 text-text-secondary">Request reagents for your projects</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setAiDialogOpen(true)}
              data-testid="import-requisitions-file-button"
            >
              <UploadSimple size={18} className="mr-2" weight="bold" />
              Import from File (AI)
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-accent-brand hover:bg-accent-brand-hover text-white" data-testid="create-requisition-button">
                <Plus size={20} className="mr-2" weight="bold" />
                New Requisition
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create Requisition</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reagent_name">Reagent Name</Label>
                  <Input
                    id="reagent_name"
                    value={formData.reagent_name}
                    onChange={(e) => setFormData({ ...formData, reagent_name: e.target.value })}
                    required
                    data-testid="reagent-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor_name">Vendor Name</Label>
                  <Input
                    id="vendor_name"
                    value={formData.vendor_name}
                    onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                    required
                    data-testid="vendor-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="catalogue_number">Catalogue Number</Label>
                  <Input
                    id="catalogue_number"
                    value={formData.catalogue_number}
                    onChange={(e) => setFormData({ ...formData, catalogue_number: e.target.value })}
                    required
                    data-testid="catalogue-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    step="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                    required
                    data-testid="quantity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project_code">Project Code</Label>
                  <Select value={formData.project_code_id} onValueChange={(value) => setFormData({ ...formData, project_code_id: value })}>
                    <SelectTrigger data-testid="project-code-select">
                      <SelectValue placeholder="Select project code" />
                    </SelectTrigger>
                    <SelectContent>
                      {projectCodes.map((pc) => (
                        <SelectItem key={pc.id} value={pc.id}>{pc.code} - {pc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-accent-brand hover:bg-accent-brand-hover text-white" data-testid="submit-requisition-button">
                  Create Requisition
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <RequisitionFileUploadDialog
          open={aiDialogOpen}
          onOpenChange={setAiDialogOpen}
          projectCodes={projectCodes}
          onSuccess={loadData}
        />

        <SearchFilterBar
          keyword={keyword}
          onKeywordChange={setKeyword}
          statusValue={statusFilter}
          onStatusChange={setStatusFilter}
          statusOptions={REQ_STATUS_OPTIONS}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onClear={() => { setKeyword(''); setStatusFilter('all'); setStartDate(''); setEndDate(''); }}
          placeholder="Search reagent, vendor, catalogue #, project…"
          testIdPrefix="emp-filter"
        />

        <div className="bg-white border border-border-default rounded">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface border-b border-border-default">
                <tr>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Reagent</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Vendor</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Catalogue #</th>
                  <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Qty</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Project</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Status</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Created</th>
                  <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequisitions.length === 0 && (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-text-tertiary">
                      No requisitions match your filters.
                    </td>
                  </tr>
                )}
                {filteredRequisitions.map((req) => (
                  <tr key={req.id} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`requisition-row-${req.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-accent-brand-subtle flex items-center justify-center">
                          <Flask size={20} className="text-accent-brand" weight="duotone" />
                        </div>
                        <div>
                          <span className="font-medium text-text-primary">{req.reagent_name}</span>
                          {(req.co_requesters?.length || 0) > 0 && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 text-xs bg-accent-brand-subtle text-accent-brand px-2 py-0.5 rounded"
                              title={`Also requested by: ${(req.co_requester_names || []).join(', ')}`}
                              data-testid={`co-requester-badge-${req.id}`}
                            >
                              +{req.co_requesters.length} joined
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-text-secondary">{req.vendor_name}</td>
                    <td className="p-4 text-text-secondary font-mono text-sm">{req.catalogue_number}</td>
                    <td className="p-4 text-text-secondary text-right font-medium" data-testid={`req-quantity-${req.id}`}>{req.quantity ?? 1}</td>
                    <td className="p-4 text-text-secondary">{req.project_code_name}</td>
                    <td className="p-4">{getStatusBadge(req.status)}</td>
                    <td className="p-4 text-text-secondary">
                      {new Date(req.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleExportRequisition(req.id)}
                          data-testid={`export-req-${req.id}`}
                        >
                          <Download size={16} className="mr-1" weight="bold" />
                          PDF
                        </Button>
                        <RequisitionDeleteButton requisition={req} onDeleted={loadData} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmployeeDashboard;
